import type {
  EditableWorkPolicyApplicationDetail,
  HistoricalWorkPolicyApplicationDetail
} from "../../../../api/work-policy-application-api.types";
import {
  WorkPolicyApplicationDraft,
  WorkPolicyApplicationPayload,
  WorkPolicyApplicationPolicySnapshot
} from "./work-policy-application.types";
import { assertValidWorkPolicyApplicationDraft } from "./work-policy-application.validation";

function policySnapshot(
  detail: HistoricalWorkPolicyApplicationDetail
): WorkPolicyApplicationPolicySnapshot {
  return {
    profileName: detail.profileName,
    dailyBaseHours: detail.dailyBaseHours,
    weeklyBaseHours: detail.weeklyBaseHours,
    weeklyOvertimeLimit: detail.weeklyOvertimeLimit,
    breakCalculationEnabled: detail.breakCalculationEnabled,
    breakWorkMinutes: detail.breakWorkMinutes,
    breakMinutes: detail.breakMinutes,
    flexibleMode: detail.flexibleMode,
    selectiveCoreEnabled: detail.selectiveCoreEnabled,
    selectiveCoreStart: detail.selectiveCoreStart,
    selectiveCoreEnd: detail.selectiveCoreEnd,
    nightStart: detail.nightStart,
    nightEnd: detail.nightEnd,
    workPolicySnapshotJson: detail.workPolicySnapshotJson
  };
}

export function deserializeWorkPolicyApplicationDetail(
  detail: EditableWorkPolicyApplicationDetail
): WorkPolicyApplicationDraft {
  const common = {
    workPolicyId: detail.workPolicyId,
    startDate: detail.startDate,
    endDate: detail.endDate,
    policySnapshot: policySnapshot(detail)
  };
  if (detail.workType === "staggered") {
    if (!detail.staggered) throw new Error("Staggered snapshot detail is missing.");
    return {
      ...common,
      workType: "staggered",
      allowedStartTimes: [detail.staggered.selectedStartTime],
      selectedStartTime: detail.staggered.selectedStartTime
    };
  }
  if (detail.workType === "selective") {
    return {
      ...common,
      workType: "selective",
      settlementStatus: "ready",
      settlementPeriods: detail.settlementPeriods.map(period => ({ ...period }))
    };
  }
  if (!detail.flexibleMode) throw new Error("Flexible snapshot mode is missing.");
  return {
    ...common,
    workType: "flexible",
    flexibleMode: detail.flexibleMode,
    schedule: detail.flexibleSchedule.map(row => ({
      workDate: row.workDate,
      weekdayNo: row.weekdayNo,
      startTime: row.startTime,
      plannedWorkMinutes: row.plannedWorkMinutes,
      breakWaiver: row.breakWaiver,
      workday: row.workday
    }))
  };
}

export type WorkPolicyApplicationHydration = {
  state: "editable";
  draft: WorkPolicyApplicationDraft;
};

export function hydrateWorkPolicyApplicationDetail(
  detail: HistoricalWorkPolicyApplicationDetail
): WorkPolicyApplicationHydration {
  return { state: "editable", draft: deserializeWorkPolicyApplicationDetail(detail) };
}

export function serializeWorkPolicyApplicationDraft(
  draft: WorkPolicyApplicationDraft
): WorkPolicyApplicationPayload {
  assertValidWorkPolicyApplicationDraft(draft);
  if (draft.workType === "staggered") {
    if (!draft.selectedStartTime) {
      throw new Error("A staggered application requires a selected start time.");
    }
    return {
      workPolicyId: draft.workPolicyId,
      workType: "staggered",
      selectedStartTime: draft.selectedStartTime
    };
  }
  if (draft.workType === "selective") {
    return {
      workPolicyId: draft.workPolicyId,
      workType: "selective"
    };
  }
  return {
    workPolicyId: draft.workPolicyId,
    workType: "flexible",
    flexibleMode: draft.flexibleMode,
    schedule: draft.schedule.map(row => ({ ...row }))
  };
}
